import { useEffect, useState } from 'react';
import { Check, Copy, Play, RefreshCw, ShieldAlert } from 'lucide-react';
import PasswordChangeCard from '../components/PasswordChangeCard.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import { api } from '../lib/api.js';

const checklist = [
  '프로젝트를 배포하고 APP_BASE_URL을 설정합니다.',
  'Meta 개발자 앱을 생성합니다.',
  '아래 OAuth Callback URL을 Meta 앱에 등록합니다.',
  'META_APP_ID, META_APP_SECRET, META_REDIRECT_URI, META_GRAPH_VERSION을 서버 환경 변수로 설정합니다.',
  'Instagram 계정을 Business 또는 Creator로 전환하고 선택할 Facebook Page에 연결합니다.',
  '운영에 필요한 Meta 권한과 앱 검수를 완료합니다.',
  'CRON_SECRET을 설정하고 외부 스케줄러가 /api/jobs/run-due를 호출하도록 구성합니다.',
  '연결 테스트와 통제된 테스트 게시 1건을 확인한 뒤 정상 운영합니다.'
];

export default function SettingsPage() {
  const [brand, setBrand] = useState(null);
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [running, setRunning] = useState(false);

  async function load() {
    try {
      const result = await api('/api/settings');
      setBrand(result.brand);
      setSetup(result.setup);
      setHashtags((result.brand?.default_hashtags || []).join(' '));
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(event) {
    event.preventDefault();
    try {
      const payload = {
        brandName: brand.brand_name,
        displayName: brand.display_name,
        toneGuide: brand.tone_guide,
        timezone: 'Asia/Seoul',
        defaultHashtags: hashtags.split(/\s+/).filter(Boolean),
        defaultLink: brand.default_link || null
      };
      const result = await api('/api/settings/brand', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setBrand(result.brand);
      setMessage('브랜드 설정을 저장했습니다.');
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function run() {
    setRunning(true);
    try {
      const result = await api('/api/jobs/run-due/manual', { method: 'POST' });
      setMessage(
        `실행 완료: 대상 ${result.dueCount}건, 성공 ${result.successCount}건, 실패 ${result.failureCount}건`
      );
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRunning(false);
    }
  }

  function copy(value) {
    navigator.clipboard.writeText(value);
    setMessage('클립보드에 복사했습니다.');
  }

  if (!brand || !setup) {
    return (
      <div className="page-wrap">
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="full-loader"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <header className="page-header">
        <div>
          <span className="eyebrow">CONFIGURATION</span>
          <h1>설정</h1>
          <p>브랜드 문구, 계정 보안과 서버 연동 준비 상태를 관리합니다.</p>
        </div>
        <button className="button button-secondary" onClick={load}>
          <RefreshCw size={16} />새로고침
        </button>
      </header>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {message ? <div className="alert alert-success">{message}</div> : null}

      <section className="settings-grid">
        <form className="panel" onSubmit={save}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow">BRAND</span>
              <h2>브랜드 기본값</h2>
            </div>
          </div>
          <label>
            브랜드명
            <input
              value={brand.brand_name}
              onChange={(event) => setBrand({ ...brand, brand_name: event.target.value })}
            />
          </label>
          <label>
            관리자 표시명
            <input
              value={brand.display_name}
              onChange={(event) => setBrand({ ...brand, display_name: event.target.value })}
            />
          </label>
          <label>
            문체 가이드
            <textarea
              rows="5"
              value={brand.tone_guide}
              onChange={(event) => setBrand({ ...brand, tone_guide: event.target.value })}
            />
          </label>
          <label>
            기본 해시태그
            <input
              value={hashtags}
              onChange={(event) => setHashtags(event.target.value)}
              placeholder="#HAAR #하르"
            />
          </label>
          <label>
            기본 링크
            <input
              type="url"
              value={brand.default_link || ''}
              onChange={(event) => setBrand({ ...brand, default_link: event.target.value })}
            />
          </label>
          <label>
            시간대
            <input value="Asia/Seoul" disabled />
          </label>
          <button className="button button-primary">설정 저장</button>
        </form>

        <article className="panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">SCHEDULER</span>
              <h2>예약 발행 상태</h2>
            </div>
            <StatusBadge value={setup.cronConfigured ? 'connected' : 'not_configured'} />
          </div>
          <div className="copy-field">
            <div>
              <span>CRON Endpoint</span>
              <code>{setup.cronEndpoint}</code>
            </div>
            <button className="icon-button" onClick={() => copy(setup.cronEndpoint)}>
              <Copy size={17} />
            </button>
          </div>
          <p className="helper">
            외부 스케줄러는 Authorization: Bearer CRON_SECRET 헤더로 호출해야 합니다.
          </p>
          <button
            className="button button-secondary"
            onClick={run}
            disabled={running || !setup.cronConfigured}
          >
            <Play size={16} />{running ? '실행 중…' : '기한 지난 예약 발행 실행'}
          </button>
          <div className="architecture-note">
            <ShieldAlert size={20} />
            <div>
              <strong>표시 원칙</strong>
              <p>CRON_SECRET이 존재해도 실제 성공 실행 전에는 대시보드에서 활성으로 표시하지 않습니다.</p>
            </div>
          </div>
        </article>

        <PasswordChangeCard />
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">DEVELOPER SETUP</span>
            <h2>Meta 연동 체크리스트</h2>
          </div>
        </div>
        <div className="setup-meta">
          <div className="copy-field">
            <div>
              <span>APP_BASE_URL</span>
              <code>{setup.appBaseUrl}</code>
            </div>
            <button className="icon-button" onClick={() => copy(setup.appBaseUrl)}>
              <Copy size={17} />
            </button>
          </div>
          <div className="copy-field">
            <div>
              <span>OAuth Callback URL</span>
              <code>{setup.callbackUrl}</code>
            </div>
            <button className="icon-button" onClick={() => copy(setup.callbackUrl)}>
              <Copy size={17} />
            </button>
          </div>
          <div className="copy-field">
            <div>
              <span>Graph API</span>
              <code>{setup.graphVersion}</code>
            </div>
          </div>
        </div>

        {setup.coreMissing.length || setup.coreErrors?.length || setup.metaMissing.length ? (
          <div className="alert alert-warning">
            <strong>누락된 서버 변수</strong>
            <span>{[
              ...setup.coreMissing,
              ...(setup.coreErrors || []),
              ...setup.metaMissing
            ].join(', ')}</span>
          </div>
        ) : (
          <div className="alert alert-success">
            <Check size={16} />화면에서 확인 가능한 필수 환경 변수 이름은 모두 설정되어 있습니다.
          </div>
        )}

        <ol className="checklist">
          {checklist.map((item, index) => (
            <li key={item}>
              <span>{index + 1}</span>
              <p>{item}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">ARCHITECTURE</span>
            <h2>구조와 책임</h2>
          </div>
        </div>
        <div className="architecture-grid">
          <div>
            <strong>브라우저</strong>
            <p>콘텐츠 작성과 상태 확인만 담당합니다. 토큰과 앱 비밀키를 저장하지 않습니다.</p>
          </div>
          <div>
            <strong>HAAR 서버</strong>
            <p>OAuth 코드 교환, 토큰 암호화, 서명 URL, 발행, 재시도, 감사 로그를 처리합니다.</p>
          </div>
          <div>
            <strong>Meta</strong>
            <p>공식 API에서 게시 처리와 지원되는 실제 지표를 반환합니다.</p>
          </div>
          <div>
            <strong>외부 CRON</strong>
            <p>보호된 실행 API를 주기적으로 호출합니다. 성공 실행은 스케줄러 기록에 남습니다.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
